import { HttpHeaders } from '../HttpHeaders';
import { HttpResponse, HttpResponseType } from '../HttpResponse';

/**
 * It accepts the raw http response and provides a typed `HttpResponse`
 */
export class ResponseAdapter<ResponseBody> implements HttpResponse<ResponseBody> {
  private isSuccessful_!: boolean;
  private code_!: number;
  private message_!: string;
  private headers_!: HttpHeaders;
  private body_?: ResponseBody;
  private responseType_?: HttpResponseType;
  private requestUrl_!: string;
  private rawResponse_!: Response;

  constructor(response: Response, url: string) {
    this.requestUrl_ = url;
    this.processResponse_(response);
  }
  /**
   * @property isSuccessful Boolean
   * States whether a request is successful or not.
   */
  get isSuccessful(): boolean {
    return this.isSuccessful_;
  }

  /**
   * @property code Boolean
   * Returns the http status code.
   */
  get code(): number {
    return this.code_;
  }

  /**
   * @property message string
   * Gets the http status message.
   */
  get message(): string {
    return this.message_;
  }

  /**
   * @property headers HttpHeaders
   * Gets the http response headers.
   */
  get headers(): HttpHeaders {
    return this.headers_;
  }

  /**
   * @property body ResponseBody
   * Gets the http response body.
   */
  get body(): ResponseBody | undefined {
    return this.body_;
  }

  /**
   * @property type HttpResponseType
   * Gets the http response type `HttpResponseType`.
   */
  get type(): HttpResponseType {
    return this.responseType_ || 'json';
  }

  /**
   * @property requestUrl string
   * Gets the http request url
   */
  get requestUrl(): string {
    return this.requestUrl_;
  }

  /**
   * @property rawResponse Response
   * Gets the http response
   */
  get rawResponse(): Response {
    return this.rawResponse_;
  }

  private processResponse_ = (response: Response): void => {
    this.rawResponse_ = response;
    this.isSuccessful_ = response.ok;
    this.code_ = response.status;
    this.message_ = response.statusText;
    this.body_ = undefined;
    this.headers_ = {};
    for (const [key, value] of response.headers.entries()) {
      this.headers_[key] = value;
    }
  };

  public async resolveBody(responseType: HttpResponseType): Promise<void> {
    this.responseType_ = responseType;
    switch (this.responseType_) {
      case 'text':
        this.body_ = (await this.rawResponse_.text()) as unknown as ResponseBody;
        break;
      case 'blob':
        this.body_ = (await this.rawResponse_.blob()) as unknown as ResponseBody;
        break;
      case 'arraybuffer':
        this.body_ = (await this.rawResponse_.arrayBuffer()) as unknown as ResponseBody;
        break;
      default:
        this.body_ = (await this.rawResponse_.json()) as ResponseBody;
        break;
    }
  }
}
